/**
 * Coaching Booking Hooks
 * React Query hooks for coaching session availability and booking
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import * as coachingService from "../services/coaching.service"

/**
 * Get available time slots for a coaching session by month/year
 */
export function useCoachingAvailability(coachingSessionId: string, month: number, year: number) {
  return useQuery({
    queryKey: ["coaching-availability", coachingSessionId, month, year],
    queryFn: () => coachingService.getCoachingAvailability(coachingSessionId, month, year),
    enabled: !!coachingSessionId && month > 0 && year > 0,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })
}

/**
 * Book a coaching session
 */
export function useBookCoachingSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      coachingSessionId,
      availabilityId,
      message,
      successUrl,
      cancelUrl,
    }: {
      coachingSessionId: string
      availabilityId: string
      message?: string
      successUrl?: string
      cancelUrl?: string
    }) =>
      coachingService.bookCoachingSession(coachingSessionId, availabilityId, message, {
        successUrl,
        cancelUrl,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coaching-availability"] })
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] })
      queryClient.invalidateQueries({ queryKey: ["user-booked-sessions"] })
    },
  })
}

/**
 * Request a custom time slot for a coaching session
 */
export function useRequestCoachingTime() {
  return useMutation({
    mutationFn: ({
      coachingSessionId,
      preferredDates,
      preferredTimes,
      timezone,
      message,
    }: {
      coachingSessionId: string
      preferredDates: string[]
      preferredTimes: string[]
      timezone: string
      message?: string
    }) =>
      coachingService.requestCoachingTime({
        coachingSessionId,
        preferredDates,
        preferredTimes,
        timezone,
        message,
      }),
  })
}

/**
 * Cancel a coaching booking
 */
export function useCancelCoachingBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ bookingId, message }: { bookingId: string; message?: string }) =>
      coachingService.cancelCoachingBooking(bookingId, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coaching-availability"] })
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] })
      queryClient.invalidateQueries({ queryKey: ["user-booked-sessions"] })
      queryClient.invalidateQueries({ queryKey: ["coaching-session-bookings"] })
    },
  })
}

/**
 * Reschedule a coaching booking
 */
export function useRescheduleCoachingBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      bookingId,
      newAvailabilityId,
      message,
    }: {
      bookingId: string
      newAvailabilityId: string
      message?: string
    }) => coachingService.rescheduleCoachingBooking(bookingId, newAvailabilityId, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coaching-availability"] })
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] })
      queryClient.invalidateQueries({ queryKey: ["user-booked-sessions"] })
      queryClient.invalidateQueries({ queryKey: ["coaching-session-bookings"] })
    },
  })
}

/**
 * Add coaching booking to Google Calendar
 */
export function useAddCoachingToCalendar() {
  return useMutation({
    mutationFn: (bookingId: string) => coachingService.addCoachingToCalendar(bookingId),
  })
}

/**
 * Initiate a refund for a coaching session booking
 */
export function useInitiateSessionRefund() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (bookingId: string) => coachingService.initiateSessionRefund(bookingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coaching-session-bookings"] })
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] })
    },
  })
}
